/**
 * Context menu for PostgreSQL partition nodes in the Object Explorer:
 * MERGE / SPLIT PARTITIONS script generation (see partitionScriptTemplates.ts
 * for the pure builders and PG19-FACTS syntax reference) plus a
 * `Select Top 1000` item on the partition leaf.
 *
 * Menu model: matches the Maintenance submenu (tableMaintenanceScripts.ts,
 * T1 REPACK) exactly — scripts are opened in a query tab (index-REBUILD
 * model), never executed directly by this module, and menu items are ALWAYS
 * offered (partition nodes only exist for pgsql to begin with) rather than
 * hidden below PG19. The generated script self-documents the PG19
 * requirement via the `-- NOTE: ... PostgreSQL 19+` warning comment when the
 * server is <19/unknown (see partitionScriptTemplates.ts). `Select Top 1000`
 * exists to restore browsing access lost when T5 moved partition children
 * out of the top-level Tables listing (regression mitigation).
 */
import { ContextMenuAction } from '../../explorer/models/TreeModel';
import type { PartitionTreeNode, PartitionsFolderNode } from '../../explorer/models/DatabaseTreeModels';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import { toast } from '../../../components/ui/toaster';
import { fetchServerVersionInfo } from '../../connections/services/serverInfoService';
import { parsePgMajorVersion } from '../../workspace/common/hooks/usePgServerMajorVersion';
import { buildSplitPartitionTemplate, buildMergePartitionsTemplate } from './partitionScriptTemplates';
import { SELECT_TOP_ROW_LIMIT } from '../../scripting/selectTopRowLimit';
import { safeConnectionAlias, safeConnectionServer } from '../../../types/safeConnectionDisplay';

export type { QualifiedName } from './partitionScriptTemplates';
export { buildSplitPartitionTemplate, buildMergePartitionsTemplate } from './partitionScriptTemplates';

export function generatePartitionContextMenuActions(
  nodeType: 'partition' | 'partitions-folder',
): ContextMenuAction[] {
  if (nodeType === 'partition') {
    return [
      { id: 'select-top-1000', label: `Select Top ${SELECT_TOP_ROW_LIMIT}`, icon: 'table' },
      { id: 'separator', label: '', isSeparator: true },
      { id: 'script-split-partition', label: 'Script SPLIT PARTITION  (PG 19+)', icon: 'code' },
    ];
  }

  return [
    { id: 'script-merge-partitions', label: 'Script MERGE PARTITIONS  (PG 19+)', icon: 'code' },
    { id: 'separator', label: '', isSeparator: true },
    { id: 'refresh', label: 'Refresh', icon: 'refresh' },
  ];
}

async function resolvePgMajor(connectionId: string, database: string): Promise<number | null> {
  const info = await fetchServerVersionInfo(connectionId, 'pgsql', database).catch(() => null);
  return info ? parsePgMajorVersion(info.version) : null;
}

async function handleSplitScript(node: PartitionTreeNode): Promise<void> {
  const { metadata } = node;
  const { activeConnection, connection, engine } = getConnectionById(metadata.connectionId);
  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }
  window.electronAPI?.analytics?.track('feature:partition_script', { engine, action: 'split' }).catch(() => {});

  const { useWorkspaceStore } = await import('../../workspace/core/store/workspaceStore');
  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const tabId = addTab({
    title: `${metadata.partitionName}_SPLIT-${getNextQueryNumber()}`,
    type: 'query',
    content: { query: '-- Building SPLIT PARTITION script...\n', isGeneratingScript: true },
    connectionInfo: {
      connectionId: metadata.connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: metadata.database,
    },
  });

  try {
    const pgMajor = await resolvePgMajor(metadata.connectionId, metadata.database);
    const script = buildSplitPartitionTemplate(
      { schema: metadata.schema, name: metadata.tableName },
      { schema: metadata.partitionSchema, name: metadata.partitionName },
      metadata.bound,
      pgMajor,
    );
    useWorkspaceStore.getState().updateTabContent(tabId, { query: script, isGeneratingScript: false });
  } catch (error) {
    useWorkspaceStore.getState().updateTabContent(tabId, {
      query: `-- Error: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
      isGeneratingScript: false,
    });
  }
}

async function handleMergeScript(node: PartitionsFolderNode): Promise<void> {
  const { connectionId, database, schema, tableName } = node.metadata;
  if (!database) {
    toast.error({ title: 'Error', description: 'Database not specified' });
    return;
  }
  const { activeConnection, connection, engine } = getConnectionById(connectionId);
  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }
  window.electronAPI?.analytics?.track('feature:partition_script', { engine, action: 'merge' }).catch(() => {});

  const { useWorkspaceStore } = await import('../../workspace/core/store/workspaceStore');
  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const tabId = addTab({
    title: `${tableName}_MERGE-${getNextQueryNumber()}`,
    type: 'query',
    content: { query: '-- Building MERGE PARTITIONS script...\n', isGeneratingScript: true },
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database,
    },
  });

  try {
    const { getPartitions } = await import('./partitionsService');
    const [partitionsResult, pgMajor] = await Promise.all([
      getPartitions(connectionId, database, schema, tableName),
      resolvePgMajor(connectionId, database),
    ]);
    if (partitionsResult.error) throw new Error(partitionsResult.error);
    if (partitionsResult.partitions.length < 2) {
      useWorkspaceStore.getState().updateTabContent(tabId, {
        query: `-- Need at least 2 partitions to merge (found ${partitionsResult.partitions.length}).\n`,
        isGeneratingScript: false,
      });
      return;
    }
    const script = buildMergePartitionsTemplate(
      { schema, name: tableName },
      partitionsResult.partitions.map((p) => ({ schema: p.schema, name: p.name })),
      pgMajor,
    );
    useWorkspaceStore.getState().updateTabContent(tabId, { query: script, isGeneratingScript: false });
  } catch (error) {
    useWorkspaceStore.getState().updateTabContent(tabId, {
      query: `-- Error: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
      isGeneratingScript: false,
    });
  }
}

/** Reuses the table-scripting select-top provider on the partition's own (child) schema/name. */
async function handleSelectTop(node: PartitionTreeNode): Promise<void> {
  const { metadata } = node;
  const { activeConnection, connection, engine } = getConnectionById(metadata.connectionId);
  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const { getScriptProvider } = await import('../../scripting');
  const { useWorkspaceStore } = await import('../../workspace/core/store/workspaceStore');
  const query = getScriptProvider(engine).generateTableScript('select-top', {
    database: metadata.database,
    schema: metadata.partitionSchema,
    tableName: metadata.partitionName,
  });

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  addTab({
    title: `Query-${getNextQueryNumber()}`,
    type: 'query',
    content: { query, autoExecuteOnMount: true },
    connectionInfo: {
      connectionId: metadata.connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: metadata.database,
    },
  });

  window.electronAPI?.analytics?.track('feature:partition_select_top', { engine }).catch(() => {});
}

export function executePartitionContextMenuAction(
  actionId: string,
  nodeId: string,
  handlers: { onRefresh?: (nodeId: string) => void } = {},
  node?: PartitionTreeNode | PartitionsFolderNode,
): void {
  if (actionId === 'refresh') {
    handlers.onRefresh?.(nodeId);
    return;
  }
  if (!node) {
    toast.error({ title: 'Error', description: 'Node not found' });
    return;
  }

  if (node.type === 'partition') {
    if (actionId === 'select-top-1000') void handleSelectTop(node);
    else if (actionId === 'script-split-partition') void handleSplitScript(node);
  } else if (node.type === 'partitions-folder') {
    if (actionId === 'script-merge-partitions') void handleMergeScript(node);
  }
}
